import { createSelector } from 'reselect'
import { SUPPORT_INDIAN_MARKET_EMAIL, SUPPORT_INDIAN_MARKET_PREFIX, SUPPORT_EMAIL_FOR_BRAND } from 'constants/support'
import { PRODUCT_TYPE_TOUR, PRODUCT_TYPE_ULTRALUX } from 'constants/offer'
import { ULTRA_LUX_TOUR } from 'constants/tours'
import { getPaymentTotals } from 'lib/payment/getPurchaseTotals'
import getCheckoutTotal from 'checkout/selectors/payment/getCheckoutTotal'
import memoize from 'lib/memoize/memoize'
import config from 'constants/config'
import { isSupportAssistantEnabled } from './featuresSelectors'
import { CRUISE_FLASH_OFFER_TYPE } from 'constants/cruise'
import { getCruiseItems } from 'checkout/selectors/cruiseSelectors'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { CONTACT_GENERAL_REGIONS_EXCLUDE_LIST, PRIORITY_SUPPORT_NUMBER_REGIONS, USE_MID_VALUE_CONTACT_TYPE_REGIONS, US_AMOUNT_REGIONS_LIST } from 'constants/config/region'
import { getCurrentTourCheckoutStep } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/tours'
import { getTollFreeSalesContactNumber } from 'checkout/utils'
import getIsLoyaltyPrioritySupportNumberEnabled from 'loyalty/selectors/getIsLoyaltyPrioritySupportNumberEnabled'
import { getViewedOrder } from 'selectors/orderSelectors'

const existingOrderTotalCalculation = memoize((order: App.Order) => {
  const propertyFees = order.bedbankItems?.length > 0 ? order.bedbankItems[0].offer.packages[0].rate.totals.propertyFees : 0
  const { grandTotal, refundedTotal, creditsTotal } = getPaymentTotals(order)
  const total = (creditsTotal + grandTotal) - refundedTotal
  return total + (propertyFees ?? 0)
})

const HIGH_VALUE_CART_AMOUNT = 4999
const HIGH_VALUE_CART_AMOUNT_US = 3999
const MID_VALUE_CART_AMOUNT = 2999
const MID_VALUE_CART_AMOUNT_US = 1999

export interface SelectorParams {
  email?: string
}

const getLastViewedOffer = createSelector(
  (state: App.State) => state.recommendations.highIntent?.lastViewed?.offerId,
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.tourV2Offers,
  (lastOfferId, offers, tourV2Offers): App.Tours.TourV2Offer | App.Offer | null => {
    if (!lastOfferId) return null

    return tourV2Offers[lastOfferId] || offers[lastOfferId]
  },
)

export const getSupportEmailForCurrentRegion = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (region) => region === SUPPORT_INDIAN_MARKET_PREFIX && config.BRAND === 'luxuryescapes' ?
    SUPPORT_INDIAN_MARKET_EMAIL :
    SUPPORT_EMAIL_FOR_BRAND[config.BRAND],
)

export const getPrioritySupportContact = createSelector(
  (state: App.State) => getCheckoutTotal(state),
  (state: App.State) => getViewedOrder(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.geo.contacts,
  (state: App.State) => state.router.location.pathname,
  (state: App.State) => getLastViewedOffer(state), // last viewed offer will be current one for the offer page
  (state: App.State) => state.checkout.cart.mode === 'change-dates' || state.checkout.cart.mode === 'select-date',
  (state: App.State) => getCruiseItems(state).length > 0,
  (state: App.State) => isSupportAssistantEnabled(state),
  (state: App.State) => getCanViewLuxPlusBenefits(state),
  (state: App.State) => !!getCurrentTourCheckoutStep(state),
  (state: App.State) => getIsLoyaltyPrioritySupportNumberEnabled(state),
  (
    grandTotal,
    existingOrder,
    currentRegionCode,
    contacts,
    pathname,
    currentOffer,
    isModifyBooking,
    isCruiseItem,
    supportAssistantEnabled,
    canViewLuxPlusBenefits,
    isTourCheckout,
    isLoyaltyPrioritySupportNumberEnabled,
  ): App.GeoContacts => {
    const isPurchasePage = pathname.includes('checkout') && pathname.includes('purchase')
    const isExistingOrder = pathname.includes('my-escapes/order') || pathname.includes('booking/confirmation')
    const existingOrderTotal = existingOrder ? existingOrderTotalCalculation(existingOrder) : 0
    const isHighValueOrder = isExistingOrder || isModifyBooking ? existingOrderTotal + grandTotal >= HIGH_VALUE_CART_AMOUNT : grandTotal >= HIGH_VALUE_CART_AMOUNT
    const isMidValueOrder = isExistingOrder || isModifyBooking ? existingOrderTotal + grandTotal >= MID_VALUE_CART_AMOUNT : grandTotal >= MID_VALUE_CART_AMOUNT
    const isToursVertical = pathname.match(/.*\/[a-z]{2}\/(?:search\/)?(tours)\??\.*/) !== null
    const isUltraLuxVertical = pathname.match(/.*\/[a-z]{2}\/(ultra-lux)\??\.*/) !== null
    const isCruiseVertical = pathname.match(/.*\/[a-z]{2}\/(cruises)\??\.*/) !== null
    const isCruiseCheckout = pathname.match(/.*\/[a-z]{2}\/checkout\/cruises\/?\.*/) !== null
    const isCruiseCabinType = pathname.match(/.*\/[a-z]{2}\/checkout\/cruise-cabin-type\/?\.*/) !== null
    const isCruiseCabinCategory = pathname.match(/.*\/[a-z]{2}\/checkout\/cruise-cabin-category\/?\.*/) !== null
    const isCruiseCabinLocation = pathname.match(/.*\/[a-z]{2}\/checkout\/cruise-cabin-location\/?\.*/) !== null
    const isCruiseTravellers = pathname.match(/.*\/[a-z]{2}\/checkout\/cruise-travellers\/?\.*/) !== null
    const isCruiseLuxPlus = pathname.match(/.*\/[a-z]{2}\/checkout\/lux-plus\/?\.*/) !== null
    const isCruiseDetails = pathname.match(/.*\/[a-z]{2}\/cruises\/\.*/) !== null
    const isTourDetails = pathname.match(/.*\/[a-z]{2}\/tour\/\.*/) !== null
    const isCruiseSetAsTour = currentOffer?.productType === PRODUCT_TYPE_TOUR && currentOffer.holidayTypes?.includes('Cruises')
    const isCruiseSetAsTourV2 = currentOffer?.productType === CRUISE_FLASH_OFFER_TYPE
    const ultraLuxProductTypes = [ULTRA_LUX_TOUR, PRODUCT_TYPE_ULTRALUX]
    const isSupportPage = pathname.match(/.*\/[a-z]{2}\/(support)\??\.*/) !== null
    const isLuxPlusLandingPage = /\/[a-z]{2}\/lux-plus(?:\/|\?|$)/.test(pathname)
    const isLuxPlusAccountPage = /\/[a-z]{2}\/account\/lux-plus(?:\/|\?|$)/.test(pathname)

    const getContactWithFallback = (contactType: App.GeoContactType) => {
      return contacts[contactType] || contacts.general
    }

    if (isSupportPage && supportAssistantEnabled) {
      return getContactWithFallback('supportAssistant')
    }

    if (!CONTACT_GENERAL_REGIONS_EXCLUDE_LIST.includes(currentRegionCode)) return contacts.general

    if (isCruiseVertical ||
      isCruiseCabinCategory ||
      isCruiseDetails ||
      isCruiseSetAsTour ||
      isCruiseSetAsTourV2 ||
      isCruiseCheckout ||
      isCruiseCabinLocation ||
      isCruiseCabinType ||
      isCruiseTravellers ||
      isCruiseLuxPlus ||
      (isCruiseItem && isPurchasePage)) {
      return getContactWithFallback('cruises')
    }

    if (isToursVertical || isTourDetails || isTourCheckout) {
      if (currentOffer?.productType == 'custom_tour') {
        return getContactWithFallback('toursPriority')
      }

      return getContactWithFallback('tours')
    }

    if (isUltraLuxVertical) {
      return getContactWithFallback('ultraLux')
    }

    const highAmount = US_AMOUNT_REGIONS_LIST.includes(currentRegionCode) ? HIGH_VALUE_CART_AMOUNT_US : HIGH_VALUE_CART_AMOUNT
    const midValueAmount = US_AMOUNT_REGIONS_LIST.includes(currentRegionCode) ? MID_VALUE_CART_AMOUNT_US : MID_VALUE_CART_AMOUNT
    if (currentOffer && ((currentOffer as App.Offer).lowestPricePackage?.price ?? 0) > highAmount) {
      return getContactWithFallback('highValue')
    }

    if (currentOffer && ultraLuxProductTypes.includes(currentOffer.productType)) {
      return getContactWithFallback('ultraLux')
    }

    if (isLoyaltyPrioritySupportNumberEnabled && !isLuxPlusLandingPage && !isLuxPlusAccountPage) {
      return getContactWithFallback('loyalty')
    }

    if (canViewLuxPlusBenefits) {
      return getContactWithFallback('luxPlus')
    }

    if (currentOffer && ((currentOffer as App.Offer).lowestPricePackage?.price ?? 0) > midValueAmount && USE_MID_VALUE_CONTACT_TYPE_REGIONS.includes(currentRegionCode)) {
      return getContactWithFallback('midValue')
    }

    if (isHighValueOrder) {
      return getContactWithFallback('highValue')
    }

    if (isMidValueOrder) {
      return getContactWithFallback('midValue')
    }

    return contacts.general
  },
)

export const getCustomerValue = createSelector(
  (state: App.State) => state.auth.customerValue,
  (state: App.State) => state.router.location.pathname,
  (customerValue, pathname): App.CustomerContactValueType | undefined => {
    const isAUOfferPage = pathname.match(/.*\/au\/(offer|tour|cruises|partner)\/\.*/) !== null
    return customerValue && isAUOfferPage ? customerValue : undefined
  },
)

export const getSupportContact = createSelector(
  (state: App.State) => state.geo.contacts,
  (state: App.State) => getPrioritySupportContact(state),
  (state: App.State) => state.router.location.pathname,
  (state: App.State) => isSupportAssistantEnabled(state),
  (state: App.State) => getCanViewLuxPlusBenefits(state),
  (state: App.State) => getCustomerValue(state),
  (state: App.State) => getIsLoyaltyPrioritySupportNumberEnabled(state),
  (
    contacts,
    priorityContact,
    pathname,
    supportAssistantEnabled,
    canViewLuxPlusBenefits,
    customerValue,
    isLoyaltyPrioritySupportNumberEnabled,
  ): App.GeoContacts => {
    if (config.BRAND !== 'luxuryescapes') return contacts.general

    const isCheckoutV1Page = pathname.includes('booking')
    const isCheckoutV2Page = pathname.includes('checkout')
    const isCheckoutPage = isCheckoutV1Page || isCheckoutV2Page
    const isOfferPage = pathname.match(/.*\/[a-z]{2}\/(offer|tour|cruises|partner)\/\.*/) !== null
    const isToursOrUltraLuxPage = pathname.match(/.*\/[a-z]{2}\/(ultra-lux|tours|cruises)\??\.*/) !== null
    const isToursSearchPage = pathname.match(/.*\/[a-z]{2}\/(search)\/(tours)\??\.*/) !== null
    const isSupportAssistant = pathname.match(/.*\/[a-z]{2}\/(support)\??\.*/) !== null && supportAssistantEnabled

    if (customerValue) {
      return contacts[customerValue] ?? contacts.general
    }

    return (isOfferPage ||
      isCheckoutPage ||
      isToursOrUltraLuxPage ||
      isToursSearchPage ||
      isSupportAssistant ||
      canViewLuxPlusBenefits ||
      isLoyaltyPrioritySupportNumberEnabled
    ) ? priorityContact : contacts.general
  },
)

export const getSupportContactNumber = createSelector(
  (state: App.State) => state.geo.currentContact?.local.humanReadable ?? '',
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getCanViewLuxPlusBenefits(state),
  (humanReadable, region, canViewLuxPlusBenefits): string => {
    if (PRIORITY_SUPPORT_NUMBER_REGIONS.includes(region)) {
      if (canViewLuxPlusBenefits) return config.PRIORITY_SUPPORT_NUMBER_LUXURY_PLUS_AU
      return config.PRIORITY_SUPPORT_NUMBER_HIGH_VALUE || humanReadable
    }

    return humanReadable
  },
)

export const getTollFreeSalesNumber = createSelector(
  (state: App.State) => state.geo.contacts,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getCanViewLuxPlusBenefits(state),
  (contacts, region): App.GeoContacts => {
    return getTollFreeSalesContactNumber(region, contacts)
  },
)

export const getLuxPlusSupportContactNumber = createSelector(
  (state: App.State) => state.geo.contacts,
  (contacts) => {
    return {
      number: contacts.luxPlus?.defaultContact.number,
      numberHumanReadable: contacts.luxPlus?.defaultContact.humanReadable,
    }
  },
)

export const getLoyaltySupportContactNumber = createSelector(
  (state: App.State) => state.geo.contacts,
  (contacts) => {
    return {
      number: contacts.loyalty?.defaultContact.number,
      numberHumanReadable: contacts.loyalty?.defaultContact.humanReadable,
    }
  },
)
